/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import React, { useState } from 'react';
import { FormGroup, TextField } from '@mui/material';
import JSG from '@cedalo/jsg-ui';
import { graphManager } from '../../../GraphManager';
import { FormattedMessage } from 'react-intl';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {intl} from '../../../helper/IntlGlobalProvider';
import SettingsPaper from '../../utils/SettingsPaper';
import { useJSGNotification } from '../../utils/JSGHooks';

let sectionType;

export default function LayoutSectionSettings(props) {
	const [minSize, setMinSize] = useState();
	const [sizeMode, setSizeMode] = useState();
	const [expanded, setExpanded] = useState();
	const [expandable, setExpandable] = useState();
	const [sectionIndex, setSectionIndex] = useState(-1);
	const [, setRandom] = useState(-1);

	const getItem = () => {
		const viewer = graphManager.getGraphViewer();
		return viewer.getSelectionProvider().getFirstSelection().getModel();
	};

	const getSection = (index, type) => {
		const node = getItem().getParent();
		if (type === 'column') {
			return node.columnData[index];
		} else if (type === 'row') {
			return node.rowData[index];
		}

		return undefined;
	};

	const onShapeSelection = () => {
		const viewer = graphManager.getGraphViewer();
		const context = viewer.getSelectionProvider().getSelectionContext();
		let index = -1;

		if (context && (context.obj === 'layoutsectioncolumn' || context.obj === 'layoutsectionrow')) {
			sectionType = context.obj === 'layoutsectioncolumn' ? 'column' : 'row';
			index = context.index;
		} else {
			const selection = viewer.getSelection();
			const item = selection.length ? selection[0].getModel() : undefined;
			if (item) {
				const layoutNode = item.getParent();
				if ((layoutNode instanceof JSG.LayoutNode)) {
					index = layoutNode.subItems.indexOf(item);
					if (sectionType === 'row') {
						index = Math.floor(index / layoutNode.columns);
					} else {
						index %= layoutNode.columns;
					}
				}
			}
		}
		setSectionIndex(() => index);

		if (index !== -1 && sectionType) {
			const data = getSection(index, sectionType);
			if (data) {
				setMinSize(() => data.minSize);
				setSizeMode(() => data.sizeMode);
				setExpanded(() => data.expanded);
				setExpandable(() => data.expandable);
			}
		}
		setRandom(() => Math.random());
	};

	useJSGNotification(JSG.SelectionProvider.SELECTION_CHANGED_NOTIFICATION, onShapeSelection, []);

	const execute = (data, oldState) => {
		const node = getItem().getParent();
		const row = sectionType === 'row';
		const cmd = new JSG.SetLayoutSectionCommand(
			node,
			sectionIndex,
			row,
			data.size,
			data.minSize,
			data.sizeMode,
			data.expandable,
			data.expanded,
			oldState
		);
		graphManager.synchronizedExecute(cmd);
	};

	const handleMinimumSize = (event) => {
		setMinSize(event.target.value);
	};

	const handleMinimumSizeBlur = (event) => {
		const data = getSection(sectionIndex, sectionType);
		const oldState = data.copy();
		data.minSize = Number(event.target.value);
		execute(data, oldState);
	};

	const handleSizeMode = (event) => {
		const data = getSection(sectionIndex, sectionType);
		const oldState = data.copy();
		data.sizeMode = event.target.value;
		setSizeMode(event.target.value);
		execute(data, oldState);
	};

	const handleExpandableChange = (event) => {
		const data = getSection(sectionIndex, sectionType);
		const oldState = data.copy();
		data.expandable = event.target.checked;
		setExpandable(event.target.checked);
		execute(data, oldState);
	};

	const handleExpandedChange = (event) => {
		const data = getSection(sectionIndex, sectionType);
		const oldState = data.copy();
		data.expanded = event.target.checked;
		setExpanded(event.target.checked);
		execute(data, oldState);
	};

	const getTitle = () => {
		return `${intl.formatMessage({ id: 'LayoutSectionSettings.Title' })} 
			${intl.formatMessage({ id: sectionType === 'row' ? 'LayoutSectionSettings.Row' : 'LayoutSectionSettings.Column' })}`;
	}

	const rowSection = sectionType === 'row';

	return (
		<SettingsPaper
			open={props.open}
			helpContext="layoutsectionsettings"
			close={() => props.close()}
			title={getTitle()}
		>
			{sectionIndex === -1 || !sectionType ? null : (
			<FormGroup>
				{rowSection && sizeMode ? (
					<TextField
						variant="outlined"
						size="small"
						fullWidth
						margin="normal"
						select
						value={sizeMode}
						onChange={(event) => handleSizeMode(event)}
						label={<FormattedMessage id="LayoutSectionSettings.SizeMode" defaultMessage="Size" />}
					>
						<MenuItem value="auto">
							<FormattedMessage id="LayoutSectionSettings.Automatic" defaultMessage="Automatic" />
						</MenuItem>
						,
						<MenuItem value="absolute">
							<FormattedMessage id="LayoutSectionSettings.Absolute" defaultMessage="Absolute" />
						</MenuItem>
					</TextField>
				) : null}
				{sizeMode !== 'absolute' ? (
					<TextField
						variant="outlined"
						size="small"
						fullWidth
						margin="normal"
						value={minSize}
						onChange={(event) => handleMinimumSize(event)}
						onBlur={(event) => handleMinimumSizeBlur(event)}
						onKeyPress={(event) => {
							if (event.key === 'Enter') {
								if (document.activeElement) {
									document.activeElement.blur();
								}
								handleMinimumSizeBlur(event);
								event.preventDefault();
							}
						}}
						label={<FormattedMessage id="LayoutSectionSettings.MinimumSize" defaultMessage="Minimum Size" />}
					/>
				) : null}
				{rowSection ? (
					<FormControlLabel
						control={<Checkbox checked={expandable} onChange={(event) => handleExpandableChange(event)} />}
						label={<FormattedMessage id="LayoutSectionSettings.Expandable" defaultMessage="Expandable" />}
					/>
				) : null}
				{rowSection && expandable ? (
					<FormControlLabel
						control={<Checkbox checked={expanded} onChange={(event) => handleExpandedChange(event)} />}
						label={<FormattedMessage id="LayoutSectionSettings.Expanded" defaultMessage="Expanded" />}
					/>
				) : null}
			</FormGroup>)}
		</SettingsPaper>
	);
}
